import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { TransactionsDetailTable } from './TransactionsDetailTable';
import { useDispatch, useSelector } from 'react-redux';
import { useMarketplace } from 'hooks';
import { Period } from '@spike/model';
import moment from 'moment-timezone';
import { useTimeZone } from 'hooks';
import Header from '../../components/Header';
import styles from './Transactions.module.scss';
import { TransactionsReportState } from 'reducers/transactionsReport/TransactionsReportState';
import { TransactionsReportRow } from 'model/TransactionsReport';
import {
    fetchTransactionsReportForExport,
    getTransactionsReport
} from 'actions/insights/sales/TransactionsReport/TransactionsReportActions';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';

interface TransactionsProps {
    description?: string;
}

export const Transactions: FunctionComponent<TransactionsProps> = props => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone).startOf('month'),
        to: moment().tz(timeZone).endOf('month')
    });

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);

    const { report, loading } = useSelector((state: any): TransactionsReportState => state.transactionsReport);

    const transactionsData: TransactionsReportRow[] = report?.rows ?? [];

    const fetchData = (p: Period | null, nextPage: number, nextSize: number) => {
        const start = p?.from ? p.from.clone().tz(timeZone).format('YYYY-MM-DD') : undefined;
        const end = p?.to ? p.to.clone().tz(timeZone).format('YYYY-MM-DD') : undefined;

        dispatch(getTransactionsReport(start, end, nextPage, nextSize) as any);
    };

    useEffect(() => {
        fetchData(period, page, size);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const transactionsCsvHeaders = [
        'Transaction Date',
        'Time',
        'Time Zone',
        'Gross Sales',
        'Discounts',
        'Refunds',
        'Net Sales',
        'Tax',
        'Tip',
        'Service Charges',
        'Total Collected',
        'Fees',
        'Net Total',
        'Event Type',
        'Transaction Status',
        'Payment Type',
        'Card Payment Method',
        'Transaction ID',
        'Appointment ID',
        'Customer ID',
        'Customer Name',
        'Pet Name',
        'Fee Percentage Rate',
        'Fee Fixed Rate',
        'Location'
    ];

    const formatMoney = (amount: number) =>
        amount < 0 ? `($${Math.abs(amount).toFixed(2)})` : `$${amount.toFixed(2)}`;

    const toCsvRows = (rows: TransactionsReportRow[]): string[][] =>
        rows.map(item => [
            item.transactionDate,
            item.timeFull,
            item.timezone,
            formatMoney(item.grossSales),
            formatMoney(item.discounts),
            formatMoney(item.refunds),
            formatMoney(item.netSales),
            formatMoney(item.tax),
            formatMoney(item.tip),
            formatMoney(item.serviceCharges),
            formatMoney(item.totalCollected),
            formatMoney(item.fees),
            formatMoney(item.netTotal),
            item.eventType ?? '',
            item.transactionStatus ?? '',
            item.paymentType ?? '',
            item.cardPaymentMethod ?? '',
            item.transactionId ?? '',
            item.appointmentId ?? '',
            item.customerId ?? '',
            item.customerName ?? '',
            item.petName ?? '',
            `${item.feePercentageRate.toFixed(3)}%`,
            formatMoney(item.feeFixedRate),
            item.location ?? ''
        ]);

    const getPeriodRange = () => ({
        start: period?.from ? period.from.clone().tz(timeZone).format('YYYY-MM-DD') : undefined,
        end: period?.to ? period.to.clone().tz(timeZone).format('YYYY-MM-DD') : undefined
    });

    const exportFile = useMemo(
        () => ({
            name: `Transactions - ${marketplace.basics.businessName}.csv`,
            fetchData: async () => {
                const { start, end } = getPeriodRange();
                const fullReport = await fetchTransactionsReportForExport(start, end);
                return { headers: transactionsCsvHeaders, data: toCsvRows(fullReport.rows) };
            }
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [period, timeZone, marketplace.basics.businessName]
    );

    const calendar = { optionSelected: 'This month' };

    const handleChangePeriod = (dateFilter: Period | null, allTime: boolean) => {
        setPage(1);

        if (allTime) {
            setPeriod(null);
            fetchData(null, 1, size);
            return;
        }

        const nextPeriod = {
            from: dateFilter?.from || moment(),
            to: dateFilter?.to || moment()
        };

        setPeriod(nextPeriod);
        fetchData(nextPeriod, 1, size);
    };

    const handlePageChange = (nextPage: number) => {
        setPage(nextPage);
        fetchData(period, nextPage, size);
    };

    const handleSizeChange = (nextSize: number) => {
        setSize(nextSize);
        setPage(1);
        fetchData(period, 1, nextSize);
    };

    return (
        <div className={styles.container}>
            <div>
                <span className={styles.description}>{props.description}</span>
            </div>

            <div className={styles.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                />
            </div>

            <TransactionsDetailTable
                loading={loading}
                report={report}
            />

            {report && report.rows.length > 0 && (
                <div className={styles.paginationWrap}>
                    <Pagination
                        page={report?.meta?.page ?? page}
                        totalPages={report?.meta?.totalPages ?? 1}
                        size={report?.meta?.perPage ?? size}
                        isLoading={loading}
                        onPageChange={handlePageChange}
                        onSizeChange={handleSizeChange}
                    />
                </div>
            )}
        </div>
    );
};

export default Transactions;
